import { lisbonDate, youthSummarySchema, type YouthSummary } from '@growup/arena-shared';
import {
  Autocomplete,
  Button,
  Card,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  type StatusTone,
  todayInLisbon,
} from '@growup/ui';
import { useCallback, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { LimitBadge, LimitSummary } from './limits';
import { RegisterYouthForm } from './RegisterYouthForm';
import { searchYouths, YouthSuggestion } from './YouthPicker';

const UNLOCK_LOOK: Record<
  YouthSummary['unlockStatus'],
  { tone: StatusTone; symbol?: string; label: string }
> = {
  ok: { tone: 'success', label: 'Pode desbloquear os PCs' },
  no_guardian_authorization: {
    tone: 'warning',
    label: 'À espera da autorização do encarregado de educação',
  },
  not_member: { tone: 'danger', label: 'Ainda não é membro' },
  no_profile: { tone: 'danger', label: 'Falta a ficha de membro' },
  deactivated: { tone: 'danger', label: 'Desativado na Arena' },
  unavailable: { tone: 'neutral', symbol: '?', label: 'A API de membros não respondeu' },
};

export function YouthsView({ arenaId, canRegister }: { arenaId: string; canRegister: boolean }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<YouthSummary | null>(null);
  const [registering, setRegistering] = useState(false);

  const reload = useCallback(
    (accountId: string) => {
      getJson(`/api/arena/arenas/${arenaId}/youths/${accountId}`, youthSummarySchema)
        .then(setSelected)
        .catch(() => setSelected(null));
    },
    [arenaId],
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
      <Card
        headingLevel={2}
        title="Jovens"
        actions={
          canRegister ? <Button onClick={() => setRegistering(true)}>Registar jovem</Button> : null
        }
      >
        <Autocomplete
          aria-label="Procurar jovem"
          placeholder="Procurar login"
          value={query}
          onChange={setQuery}
          search={(q) => searchYouths(arenaId, q)}
          getKey={(y) => y.accountId}
          renderItem={(y) => <YouthSuggestion youth={y} />}
          onSelect={(y) => {
            setRegistering(false);
            setSelected(y);
            setQuery(y.login);
          }}
        />
      </Card>
      {registering ? (
        <RegisterYouthForm
          arenaId={arenaId}
          onDone={(result) => {
            setRegistering(false);
            if (result) reload(result.accountId);
          }}
        />
      ) : selected ? (
        <YouthDetail
          arenaId={arenaId}
          youth={selected}
          onChanged={() => reload(selected.accountId)}
        />
      ) : (
        <p className="text-body text-gu-ink-muted">
          Procure um jovem pelo login, ou registe um novo.
        </p>
      )}
    </div>
  );
}

function YouthDetail({
  arenaId,
  youth,
  onChanged,
}: {
  arenaId: string;
  youth: YouthSummary;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [meritUntil, setMeritUntil] = useState(() => `${lisbonDate(new Date()).slice(0, 4)}-12-31`);
  const [minutes, setMinutes] = useState(30);

  const run = async (method: string, path: string, body: unknown, message: string) => {
    setError(null);
    setDone(null);
    try {
      await send(method, path, body);
      setDone(message);
      onChanged();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const base = `/api/arena/youths/${youth.accountId}`;

  return (
    <Card headingLevel={2} title={youth.login}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <LimitBadge status={youth.limits} />
          <LimitSummary status={youth.limits} />
          <StatusBadge
            tone={UNLOCK_LOOK[youth.unlockStatus].tone}
            {...(UNLOCK_LOOK[youth.unlockStatus].symbol
              ? { symbol: UNLOCK_LOOK[youth.unlockStatus].symbol }
              : {})}
          >
            {UNLOCK_LOOK[youth.unlockStatus].label}
          </StatusBadge>
          <p className="text-body text-gu-ink-muted">
            {youth.isAssociate ? '★ Associado (tempo extra)' : 'Membro'}
            {youth.meritUntil ? ` · Bom aproveitamento até ${youth.meritUntil}` : ''}
          </p>
        </div>

        {error ? <Notice tone="danger">{error}</Notice> : null}
        {done ? <Notice tone="success">{done}</Notice> : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Field
              label="Bom aproveitamento escolar"
              hint="Dá tempo extra até à data indicada. Não se guardam notas."
            >
              <DateInput value={meritUntil} onChange={setMeritUntil} min={todayInLisbon()} />
            </Field>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() =>
                  void run('PUT', `${base}/merit`, { validUntil: meritUntil }, 'Marca guardada.')
                }
              >
                Marcar
              </Button>
              {youth.meritUntil ? (
                <Button
                  variant="secondary"
                  onClick={() => void run('DELETE', `${base}/merit`, undefined, 'Marca retirada.')}
                >
                  Retirar
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Field label="Tempo extra hoje" hint="Só sem lista de espera nesta arena.">
              <Select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {[15, 30, 45, 60, 90].map((m) => (
                  <option key={m} value={m}>
                    {m} min
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              variant="secondary"
              onClick={() =>
                void run('POST', `${base}/grants`, { arenaId, minutes }, 'Tempo extra dado.')
              }
            >
              Dar tempo extra
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <Field label="Novo PIN" hint="4 dígitos. O jovem escolhe-o na hora.">
              <Input
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            <Button
              variant="secondary"
              disabled={pin.length !== 4}
              onClick={() => {
                void run('PUT', `${base}/pin`, { pin }, 'PIN alterado.');
                setPin('');
              }}
            >
              Mudar PIN
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-body text-gu-ink-muted">Lista de espera</span>
            <Button
              variant="secondary"
              onClick={() =>
                void run(
                  'POST',
                  `/api/arena/arenas/${arenaId}/waitlist`,
                  { youthAccountId: youth.accountId },
                  'Posto na fila.',
                )
              }
            >
              Pôr na fila
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
